import { Chip, Stack, SxProps, Tooltip, tooltipClasses } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { TestValueWrapper } from '../../utils/TestValueWrapper';
import { stopPropagationHandler } from '../../utils/handlers';

// Style to hide a chip but preserve its offsetWidth
// `display: none;` would not render the chip at all, hence offsetWidth = 0
// `visibility: hidden;` alone would make the chip invisible but still take space
const hiddenSx: SxProps = {
  '& .hidden': { visibility: 'hidden', position: 'absolute', left: '-10000px' },
  '& .truncate': { maxWidth: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
};

// Stack spacing is rendered as a margin on the chip and contributes to its total width.
// `offsetWidth` does not account for margins, so we need to do it manually
const STACK_SPACING_PX = 8; // The pixel equivalent to STACK_SPACING_SX
const STACK_SPACING_SX = 1; // The stack spacing according to MUI units

const getChipWidth = (chip: HTMLElement | null) => {
  return (chip?.offsetWidth || 0) + STACK_SPACING_PX;
};

export const TagCell = ({ tags = [] }: { tags: string[] }) => {
  const [visibleTags, setVisibleTags] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const plusChipRef = useRef<HTMLDivElement | null>(null);

  // To avoid triggering the useEffect when the reference to the input values
  // changes, we use a serialized dependency instead. Using `value.width` as
  // dependency in useEffect would not account for changes in the array elements,
  // which could impact the width computations.
  const depValue = JSON.stringify(tags);

  useEffect(() => {
    if (tags.length === 0 || !contentRef.current || chipRefs.current.length === 0 || !plusChipRef?.current) return;

    if (tags.length === 1) {
      // If there is only one tag it has to be displayed even if it is too long.
      // Force all tags to be visible to keep the `+1` tag hidden.
      setVisibleTags(1);
      return;
    }

    const availableWidth = contentRef.current.offsetWidth;
    const plusChipWidth = getChipWidth(plusChipRef.current);

    let usedWidth = 0;
    let fitTags = 0;

    // Compute how many tags fit in the current cell width
    for (const tag of chipRefs.current) {
      const tagWidth = getChipWidth(tag);
      if (usedWidth + tagWidth < availableWidth) {
        usedWidth += tagWidth;
        fitTags++;
      }
    }

    // If not all tags fit, make sure the tooltip chip fits by removing other chips
    if (fitTags < tags.length) {
      while (usedWidth + plusChipWidth > availableWidth) {
        fitTags--;
        usedWidth -= getChipWidth(chipRefs.current[fitTags]);
      }
    }

    // During unit tests where the DOM is not rendered, we need to force
    // the component to display all tags, or their content will not be seen
    fitTags = TestValueWrapper.wrap(fitTags, tags.length);

    // Set how many tags are visible, accounting for the tooltip tag if necessary
    setVisibleTags(fitTags);
  }, [depValue, contentRef?.current?.offsetWidth, chipRefs.current.length, plusChipRef?.current?.offsetWidth, tags.length]);

  return (
    <Stack ref={contentRef} direction="row" spacing={STACK_SPACING_SX} sx={{ width: '100%', ...hiddenSx }}>
      {tags.map((tag, index) => (
        <Chip
          key={tag}
          label={tag}
          className={tags.length === 1 ? 'truncate' : index >= visibleTags ? 'hidden' : ''}
          ref={(element) => (chipRefs.current[index] = element)}
          clickable={false}
          onClick={stopPropagationHandler}
        />
      ))}
      <Tooltip
        componentsProps={{ popper: { sx: { [`& .${tooltipClasses.tooltip}`]: { backgroundColor: 'common.white', boxShadow: 1 }, ...hiddenSx } } }}
        title={
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: STACK_SPACING_SX, justifyContent: 'center' }}>
            {tags.map((tag: string, index: number) => (
              <Chip key={tag} label={tag} className={index < visibleTags ? 'hidden' : ''} clickable={false} onClick={stopPropagationHandler} />
            ))}
          </Stack>
        }
      >
        <Chip ref={plusChipRef} label={`+ ${tags.length - visibleTags}`} className={tags.length <= visibleTags ? 'hidden' : ''} />
      </Tooltip>
    </Stack>
  );
};
